import type {
    Account,
    MoneyMapSimulation,
    Rule,
    RuleSet,
} from '@fruitful/money-map-simulator';
import type {
    MoneyMapAccountWithAccountDetails,
    MoneyMapRuleGroup,
    MoneyMapRuleWithAccount,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import {
    HolisticCategory,
    MoneyMapAccountType,
    RuleSetType,
    RuleType,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';

function inferAccountType(
    mmAccount: MoneyMapAccountWithAccountDetails,
): Account['type'] {
    if (
        mmAccount.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT ||
        mmAccount.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT
    ) {
        const details = mmAccount.accountDetails;
        if ('apexAccountType' in details) {
            const apex = details.apexAccountType;
            const rothTypes = new Set<SupportedApexAccountRegistrationType>([
                SupportedApexAccountRegistrationType.RegistrationIraRoth,
                SupportedApexAccountRegistrationType.RegistrationCustodialIraRoth,
                SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryRoth,
            ]);
            const tradTypes = new Set<SupportedApexAccountRegistrationType>([
                SupportedApexAccountRegistrationType.RegistrationIraTraditional,
                SupportedApexAccountRegistrationType.RegistrationIraRollover,
                SupportedApexAccountRegistrationType.RegistrationIraBeneficiaryTraditional,
                SupportedApexAccountRegistrationType.RegistrationIraSep,
                SupportedApexAccountRegistrationType.RegistrationIraSimple,
            ]);
            if (apex != null && rothTypes.has(apex)) return 'ROTH_IRA';
            if (apex != null && tradTypes.has(apex)) return 'TRADITIONAL_IRA';
        }
        return 'TAXABLE_INVESTMENT';
    }

    if ('holisticCategory' in mmAccount.accountDetails) {
        const category = mmAccount.accountDetails.holisticCategory;
        if (category === HolisticCategory.BORROW) return 'DEBT';
    }

    if ('accountType' in mmAccount.accountDetails) {
        const accountType = mmAccount.accountDetails.accountType;
        if (typeof accountType === 'string') {
            const upper = accountType.toUpperCase();
            if (upper === 'SAVINGS' || upper.includes('SAVING'))
                return 'SAVINGS';
            if (upper === 'CHECKING' || upper.includes('CHECK'))
                return 'CHECKING';
        }
    }

    return 'CHECKING';
}

function buildAccount(mmAccount: MoneyMapAccountWithAccountDetails): Account {
    const simType = inferAccountType(mmAccount);
    const name =
        mmAccount.accountDetails.name ||
        mmAccount.accountDetails.institutionName;

    const base = {
        id: mmAccount.id,
        name,
    };

    if (simType === 'DEBT') {
        const details = mmAccount.accountDetails;
        return {
            ...base,
            type: 'DEBT',
            balanceCents: 0,
            annualInterestRate:
                'interestRate' in details && details.interestRate != null
                    ? details.interestRate / 100
                    : 0,
            minimumPaymentCents:
                'minimumPayment' in details && details.minimumPayment != null
                    ? details.minimumPayment
                    : 0,
        };
    }

    if (simType === 'ROTH_IRA' || simType === 'TRADITIONAL_IRA') {
        return {
            ...base,
            type: simType,
            balanceCents: 0,
            annualContributionLimitCents: 750000,
            initialYtdContributionCents: 0,
        };
    }

    if (simType === 'TAXABLE_INVESTMENT') {
        return {
            ...base,
            type: 'TAXABLE_INVESTMENT',
            balanceCents: 0,
        };
    }

    return {
        ...base,
        type: simType,
        balanceCents: 0,
    };
}

function inferRuleType(mmRule: MoneyMapRuleWithAccount): RuleType {
    if (mmRule.goalType === 'DEBT_PAY_DOWN') return RuleType.DebtPayDown;
    return RuleType.Savings;
}

function buildRule(mmRule: MoneyMapRuleWithAccount): Rule | null {
    const base = {
        id: mmRule.id,
        type: inferRuleType(mmRule),
        description: mmRule.description,
        priority: mmRule.priority,
        accountId: mmRule.moneyMapAccountId,
        splitPercentage: 1.0,
        monthlyContributionMaxCents:
            mmRule.monthlyContributionMaxCents ?? undefined,
        yearlyContributionMaxCents:
            mmRule.yearlyContributionMaxCents ?? undefined,
    };

    if (mmRule.goalType === 'DEBT_PAY_DOWN') {
        return {
            ...base,
            accountId: undefined,
            accountIds: [mmRule.moneyMapAccountId],
            allocationType: 'DEBT_PAY_DOWN',
            strategy: 'AVALANCHE',
        };
    }

    if (mmRule.goalType === 'BUILD_UP_TARGET_BALANCE') {
        return {
            ...base,
            allocationType: 'MINIMUM_BALANCE',
            targetBalanceCents: mmRule.targetBalanceCents ?? 0,
        };
    }

    if (mmRule.percentage != null) {
        return {
            ...base,
            allocationType: 'PERCENTAGE',
            percentage: mmRule.percentage / 100,
        };
    }

    // fixedAmountCents is a legacy paycheck-routing field (route $X per paycheck).
    // It doesn't map cleanly to the simulator; we approximate it as a target balance.
    if (mmRule.fixedAmountCents != null) {
        return {
            ...base,
            allocationType: 'MINIMUM_BALANCE',
            targetBalanceCents: mmRule.fixedAmountCents,
        };
    }

    return {
        ...base,
        allocationType: 'PERCENTAGE',
        percentage: 1.0,
    };
}

function groupRulesIntoRuleSets(
    moneyMap: MoneyMapWithRulesAndAccounts,
): RuleSet[] {
    const groups = moneyMap.moneyMapRuleGroups;
    const rules = moneyMap.moneyMapRules;

    if (groups.length > 0) {
        return buildFromGroups(groups, rules);
    }

    return buildFromFlatRules(rules);
}

function normalizeSplitPercentages(rules: Rule[]): Rule[] {
    if (rules.length === 0) return rules;
    const equal = 1.0 / rules.length;
    return rules.map((r) => ({ ...r, splitPercentage: equal }));
}

function buildFromGroups(
    groups: MoneyMapRuleGroup[],
    allRules: MoneyMapRuleWithAccount[],
): RuleSet[] {
    return groups
        .sort((a, b) => a.priority - b.priority)
        .map((group, idx) => {
            const groupRules = normalizeSplitPercentages(
                allRules
                    .filter((r) => r.moneyMapRuleGroupId === group.id)
                    .sort((a, b) => a.priority - b.priority)
                    .map(buildRule)
                    .filter((r): r is Rule => r != null),
            );

            return {
                id: group.id,
                type: RuleSetType.FinancialHealth,
                name: group.name,
                priority: idx + 1,
                rules: groupRules,
            };
        })
        .filter((rs) => rs.rules.length > 0);
}

function buildFromFlatRules(rules: MoneyMapRuleWithAccount[]): RuleSet[] {
    const byType = new Map<string, MoneyMapRuleWithAccount[]>();
    for (const rule of rules) {
        const key = rule.type;
        const existing = byType.get(key) ?? [];
        existing.push(rule);
        byType.set(key, existing);
    }

    const typePriority: Record<string, number> = {
        BILLS: 1,
        SPEND: 2,
        GOAL: 3,
        ACCOUNT_ONLY: 4,
    };

    return Array.from(byType.entries())
        .sort(([a], [b]) => (typePriority[a] ?? 99) - (typePriority[b] ?? 99))
        .map(([ruleType, typeRules], idx) => ({
            id: `rs-${ruleType.toLowerCase()}`,
            type: RuleSetType.FinancialHealth,
            name: ruleType.charAt(0) + ruleType.slice(1).toLowerCase(),
            priority: idx + 1,
            rules: normalizeSplitPercentages(
                typeRules
                    .sort((a, b) => a.priority - b.priority)
                    .map(buildRule)
                    .filter((r): r is Rule => r != null),
            ),
        }))
        .filter((rs) => rs.rules.length > 0);
}

/**
 * Transform a grove-app MoneyMapWithRulesAndAccounts into the simulator's
 * MoneyMapSimulation format. Account balances default to 0 (the user
 * should fill them in since the backend doesn't track running balances
 * for holistic/external accounts).
 */
export function transformToSimulation(
    moneyMap: MoneyMapWithRulesAndAccounts,
    accountDetails: MoneyMapAccountWithAccountDetails[],
): MoneyMapSimulation {
    const accountMap = new Map(accountDetails.map((a) => [a.id, a]));

    const referencedAccountIds = new Set<string>();
    referencedAccountIds.add(moneyMap.incomeMoneyMapAccountId);
    for (const rule of moneyMap.moneyMapRules) {
        referencedAccountIds.add(rule.moneyMapAccountId);
    }

    const accounts: Account[] = [];
    for (const accountId of referencedAccountIds) {
        const detail = accountMap.get(accountId);
        if (detail) {
            accounts.push(buildAccount(detail));
        } else {
            accounts.push({
                id: accountId,
                name: `Account ${accountId.slice(-6)}`,
                type: 'CHECKING',
                balanceCents: 0,
            });
        }
    }

    const ruleSets = groupRulesIntoRuleSets(moneyMap);

    return {
        monthlyGoalsCents: moneyMap.monthlyIncomeCents,
        incomeAccountId: moneyMap.incomeMoneyMapAccountId,
        ruleSets,
        accounts,
    };
}
